import { backendPublicPath } from "@/api/servicePaths";
import { TtlPromiseCache } from "@/lib/cache/ttlPromiseCache";

import FETCHRAW, { type FetchRawResponse } from "../fetchRaw";

export type SignupProvisioningStatus =
  | "pending"
  | "running"
  | "ready"
  | "failed_retryable"
  | "failed_terminal"
  | "cancelled"
  | "completed"
  | "skipped"
  | string;

export interface SignupProvisioningStep {
  step_key?: string;
  step_label?: string | null;
  status?: SignupProvisioningStatus;
  attempt_count?: number;
  message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  last_error_code?: string | null;
  last_error?: string | null;
}

export interface SignupProvisioningContent {
  tenant_id?: string | null;
  tenant_display_string?: string | null;
  status?: SignupProvisioningStatus | null;
  ready?: boolean | null;
  access_ready?: boolean | null;
  retryable?: boolean | null;
  run_id?: string | null;
  current_step?: string | null;
  steps?: SignupProvisioningStep[] | null;
  message?: string | null;
  next_retry_at?: string | null;
}

export interface SignupProvisioningResponse extends FetchRawResponse {
  content: SignupProvisioningContent | null;
}

export interface SignupProvisioningCacheContext {
  tenantGuid?: string | null;
  userGuid?: string | null;
}

interface SignupProvisioningRequestOptions extends SignupProvisioningCacheContext {
  force?: boolean;
  suppressAuthLogoutOn401?: boolean;
}

const READY_STATUSES = new Set(["ready", "completed", "skipped"]);

const statusCache = new TtlPromiseCache<SignupProvisioningResponse>(5_000);

const buildStatusCacheKey = (context?: SignupProvisioningCacheContext): string => {
  const tenantGuid = String(context?.tenantGuid || "").trim();
  const userGuid = String(context?.userGuid || "").trim();
  return `${tenantGuid || "no-tenant"}:${userGuid || "no-user"}`;
};

const normalizeSignupProvisioningStatus = (value?: string | null): string => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const normalizeProvisioningResponse = (response: SignupProvisioningResponse): SignupProvisioningResponse => {
  const content = response?.content;
  if (content && typeof content === "object" && "content" in content) {
    const nestedContent = (content as { content?: unknown }).content;
    if (nestedContent && typeof nestedContent === "object") {
      return { ...response, content: nestedContent as SignupProvisioningContent };
    }
  }
  return response;
};

export const isSignupProvisioningReady = (content?: SignupProvisioningContent | null): boolean => {
  if (!content) {
    return false;
  }

  if (content.ready === true) {
    return true;
  }

  return READY_STATUSES.has(normalizeSignupProvisioningStatus(content.status));
};

const fetchSignupProvisioningStatus = async (
  options?: SignupProvisioningRequestOptions
): Promise<SignupProvisioningResponse> => {
  const cacheKey = buildStatusCacheKey(options);
  const fetcher = async () => {
    const response = (await FETCHRAW({
      type: "apiurl",
      path: backendPublicPath("/signup/provisioning/status"),
      method: "GET",
      suppressAuthLogoutAfterRefresh401: Boolean(options?.suppressAuthLogoutOn401)
    })) as SignupProvisioningResponse;
    return normalizeProvisioningResponse(response);
  };

  return options?.force ? statusCache.getFresh(cacheKey, fetcher) : statusCache.get(cacheKey, fetcher);
};

export const getSignupProvisioningStatus = async (
  options?: SignupProvisioningRequestOptions
): Promise<SignupProvisioningResponse> => {
  return fetchSignupProvisioningStatus(options);
};

export const retrySignupProvisioning = async (
  context?: SignupProvisioningCacheContext & { suppressAuthLogoutOn401?: boolean }
): Promise<SignupProvisioningResponse> => {
  const response = (await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/signup/provisioning/retry"),
    method: "POST",
    suppressAuthLogoutAfterRefresh401: Boolean(context?.suppressAuthLogoutOn401)
  })) as SignupProvisioningResponse;
  statusCache.delete(buildStatusCacheKey(context));
  return normalizeProvisioningResponse(response);
};

export const clearSignupProvisioningStatusCache = (context?: SignupProvisioningCacheContext) => {
  if (!context) {
    statusCache.clear();
    return;
  }

  statusCache.delete(buildStatusCacheKey(context));
};
